import { DateHelper } from '@bryntum/scheduler';
import DateTimeHelper from 'infrastructure/js/utils/dateTimeHelper.js';
import highPriorityIcon from 'svg/high-priority-task-icon.svg?url';
import { createLabelHelper } from 'infrastructure/js/utils/labelHelper';
import ResourceColumn from './Components/ResourceColumn/resourceColumn';
import { pdfInitConfig } from './Configs/PDFPrintConfig';

export function getSchedulerConfig() {
  return {
    rowHeight: 24,
    eventStyle: 'border',
    eventRenderer,
    eventTooltipFeature: { disabled: true },
    eventEditFeature: { disabled: true },
    barMargin: 0,
    timeResolution: 1,
    viewPreset: 'pl_one_week',
    presets: getCustomPresets(),
    zoomKeepsOriginalTimespan: true, // TODO: check potential performance issue
    stickyHeaders: false,
    highlightSuccessors: true,
    highlightPredecessors: true,
    createEventOnDblClick: false,
    dependencyEditFeature: false,
    dependenciesFeature: { allowCreate: false },
    resourceTimeRangesFeature: true,
    timeRangesFeature: true,
    eventDragCreateFeature: false,
    eventDragFeature: {
      validatorFn: validateEventDrag,
    },
    printFeature: pdfInitConfig,
    eventResizeFeature: false,
    sortFeature: false,
    headerMenuFeature: false,
    cellMenuFeature: false,
    timeAxisHeaderMenuFeature: false,
    scheduleMenuFeature: false,
    columns: [
      {
        headerRenderer: columnHeaderRenderer,
        renderer: (data) => <ResourceColumn {...data} />,
        field: 'name',
        width: 165,
        editor: false,
        totalUtl: '',
      },
    ],
  };
}

//--------- Renderers -----------
//////////////////////////////////

// Event renderer does not return JSX because of performance issues
function eventRenderer(data) {
  let itemData = data?.eventRecord?.data;
  let renderData = data?.renderData;

  let fontSize = Math.floor(renderData?.height - 8);
  let style = `font-size:${fontSize}px`;
  let iconStyle = `height: ${fontSize}px;`;

  let eventIcon;
  if (itemData.valid) {
    eventIcon =
      itemData?.priority === 'High'
        ? `<img src="${highPriorityIcon}" alt="Priority Icon" style="${iconStyle}"/>`
        : `<div class="b-sch-event-icon ${itemData.eventIcon}"></div>`;
  } else {
    eventIcon = `<div class="b-sch-event-icon ${itemData.eventIcon}"></div>`;
  }

  return `<div class="b-sch-event-custom" style="${style}">
             ${eventIcon}
              <div class="b-sch-event-body">${itemData.name}</div>
            </div>`;
}

// Header renderer does not return JSX because it is not supported for this feature (ver 5.6.0).
function columnHeaderRenderer(data) {
  const labels = createLabelHelper('mat.scheduler.gantt.');
  const totalUtl = data.column?.data?.totalUtl;
  let resourcesHeader = labels.get('column.stations');
  return `<div>
        <div class="column-header-resource">${resourcesHeader}</div>
        <div class="column-header-utl">
            <span class="column-header-utl-value">${totalUtl ?? ''}%</span>
            <span>${labels.get('column.totalUtl')}</span>
            </div>
      </div>`;
}

//----------- Helpers --------------
/////////////////////////////////////

function getCustomPresets() {
  let windowWidth = Math.max(window.innerWidth, 1280);
  let schWidth = windowWidth - 300;
  const labels = createLabelHelper('mat.scheduler.gantt.');

  return [
    {
      id: 'pl_two_weeks',
      base: 'hourAndDay',
      name: labels.get('horizon.twoWeeks'),
      tickWidth: Math.floor(schWidth / 14),
      shiftIncrement: 2,
      shiftUnit: 'week',
      defaultSpan: 15,
      mainHeaderLevel: 0,
      headers: [
        {
          unit: 'W',
          increment: 2,
          renderer: function (start, end) {
            let startDate = DateHelper.format(start, DateTimeHelper.getDateFormat());
            let endDate = DateHelper.format(DateHelper.getEndOfPreviousDay(end), DateTimeHelper.getDateFormat());
            return `${startDate} - ${endDate}`;
          },
        },
        {
          unit: 'day',
          increment: 1,
          dateFormat: getDayFormat(),
        },
      ],
    },
    {
      id: 'pl_one_week',
      base: 'hourAndDay',
      name: labels.get('horizon.oneWeek'),
      tickWidth: Math.floor(schWidth / 7),
      shiftIncrement: 1,
      shiftUnit: 'week',
      defaultSpan: 15,
      mainHeaderLevel: 0,
      headers: [
        {
          unit: 'W',
          increment: 1,
          renderer: function (start, end) {
            let startDate = DateHelper.format(start, DateTimeHelper.getDateFormat());
            let endDate = DateHelper.format(DateHelper.getEndOfPreviousDay(end), DateTimeHelper.getDateFormat());
            return `${startDate} - ${endDate}`;
          },
        },
        {
          unit: 'day',
          increment: 1,
          dateFormat: 'ddd ' + getDayFormat(),
        },
      ],
    },
    {
      id: 'pl_three_days',
      base: 'hourAndDay',
      name: labels.get('horizon.threeDays'),
      tickWidth: Math.floor(schWidth / 9),
      shiftIncrement: 3,
      shiftUnit: 'day',
      defaultSpan: 105,
      mainHeaderLevel: 0,
      headers: [
        {
          unit: 'day',
          increment: 3,
          renderer: function (start, end) {
            let startDate = DateHelper.format(start, DateTimeHelper.getDateFormat());
            let endDate = DateHelper.format(DateHelper.getEndOfPreviousDay(end), DateTimeHelper.getDateFormat());
            return `${startDate} - ${endDate}`;
          },
        },
        {
          unit: 'hour',
          increment: 8,
          dateFormat: 'HH:mm ddd ' + getDayFormat(),
        },
      ],
    },
    {
      id: 'pl_one_day',
      base: 'hourAndDay',
      name: labels.get('horizon.oneDay'),
      tickWidth: Math.floor(schWidth / 12),
      shiftIncrement: 1,
      shiftUnit: 'day',
      defaultSpan: 105,
      mainHeaderLevel: 0,
      headers: [
        {
          unit: 'day',
          increment: 1,
          dateFormat: DateTimeHelper.getDateFormat(),
        },
        {
          unit: 'hour',
          increment: 2,
          dateFormat: 'HH:mm',
        },
      ],
    },
  ];
}

function getDayFormat() {
  let format = DateTimeHelper.getDateFormat();
  switch (format) {
    case 'DD/MM/YYYY':
      return 'DD/MM';
    case 'M/D/YYYY':
    case 'M/D/YY':
      return 'M/D';

    case 'YY/MM/DD':
    case 'MM/DD/YYYY':
    case 'MM/DD/YY':
      return 'MM/DD';

    case 'DD-MMM-YYYY':
    case 'DD-MM-YYYY':
    case 'YYYY-MM-DD':
      return 'DD MMM';
  }
  console.error('Unknown Date format: ' + format);
  return 'DD MMM';
}

function validateEventDrag({ startDate, eventRecords, newResource }) {
  const labels = createLabelHelper('mat.scheduler.gantt.');
  if (eventRecords?.[0].data?.isLockedForResource) {
    //currently no validation for non-operational task
    return {
      valid: true,
      message: '',
    };
  }

  let draggedOperationId = eventRecords?.[0].data?.operation?.id || '';
  if (draggedOperationId) {
    if (newResource?.data?.operationIds?.includes(draggedOperationId)) {
      //NOTE: currently there is no support for 'Start date in the past'
      // if (startDate?.getTime() < new Date().getTime()) {
      //   // console.log('----------------', startDate?.getTime(), new Date().getTime())
      //   return {
      //     valid: true,
      //     message: 'Start date in the past - ' + moment(startDate).format(DateTimeHelper.getDateTimeFormat())
      //   }
      // }
      return {
        valid: true,
        message: '',
      };
    }
  }

  let labelObj = {
    operationName: eventRecords?.[0].data?.operation?.businessId || '',
    taskName: eventRecords?.[0].data?.name || '',
    resourceName: newResource?.data?.businessId || '',
  };

  return {
    valid: false,
    message: labels.get('task.drag.validation', '', labelObj),
  };
}
